/**
 * Loading / error / "no trip" states shared by every page so the app never
 * renders blank. All copy describes what is actually known.
 */
import { EmptyState, StatusBadge } from '@pazportal/ui';
import { useRecentTrips, type ActiveTrip } from '../hooks/useActiveTrip';
import { errorMessage } from '../lib/errors';
import { formatDate } from '../lib/formatters';
import { TripPicker } from './TripPicker';

export function LoadingBlock({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="flex h-48 items-center justify-center" role="status" aria-live="polite">
      <div className="flex flex-col items-center gap-3">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" aria-hidden="true" />
        <span className="text-sm text-text-muted">{label}</span>
      </div>
    </div>
  );
}

export function ErrorBlock({
  title,
  error,
  onRetry,
}: {
  title: string;
  error: unknown;
  onRetry?: () => void;
}) {
  return (
    <div role="alert" className="rounded-lg border border-error/40 bg-error-soft p-4">
      <p className="text-sm font-semibold text-error">{title}</p>
      <p className="mt-1 break-words text-sm text-text">{errorMessage(error)}</p>
      {onRetry && (
        <button type="button" onClick={onRetry} className="btn-secondary mt-3">
          Try again
        </button>
      )}
    </div>
  );
}

function TripSummaryCard({ trip }: { trip: ActiveTrip }) {
  return (
    <li className="card p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-text">{trip.location?.name ?? trip.customer?.name ?? trip.project_number}</p>
          <p className="truncate text-xs text-text-muted">
            <span className="font-mono">{trip.project_number}</span>
            {trip.customer?.name ? ` · ${trip.customer.name}` : ''}
          </p>
        </div>
        <StatusBadge status={trip.status ?? 'completed'} />
      </div>
      <p className="mt-2 text-xs text-text-muted">
        {formatDate(trip.start_date)} – {formatDate(trip.end_date)}
      </p>
      {trip.scope && <p className="mt-1 line-clamp-2 text-xs text-text">{trip.scope}</p>}
    </li>
  );
}

/**
 * Rendered by every page when no trip is selected.
 *  - Active trips exist but none is chosen → picker.
 *  - Nothing scheduled or in progress → explicit empty state plus the most
 *    recent completed trips, read-only, so the screen is never blank.
 */
export function NoActiveTrip({
  trips,
  onSelect,
  error,
  onRetry,
}: {
  trips: ActiveTrip[];
  onSelect: (id: string) => void;
  error?: unknown;
  onRetry?: () => void;
}) {
  const recent = useRecentTrips(!error && trips.length === 0);

  if (error) {
    return (
      <div className="mx-auto max-w-lg">
        <ErrorBlock title="Could not load field trips" error={error} onRetry={onRetry} />
      </div>
    );
  }

  if (trips.length > 0) {
    return (
      <div className="mx-auto max-w-lg space-y-4">
        <div>
          <h1 className="text-xl font-bold text-text">Choose a field trip</h1>
          <p className="mt-1 text-sm text-text-muted">
            {trips.length} scheduled or in-progress {trips.length === 1 ? 'trip is' : 'trips are'} available to you.
          </p>
        </div>
        <TripPicker trips={trips} selectedId={undefined} onSelect={onSelect} />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-lg space-y-6">
      <EmptyState
        icon={
          <svg className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3M3 11h18M5 7h14a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V9a2 2 0 012-2z" />
          </svg>
        }
        title="No active field trip"
        subtitle="Nothing is scheduled or in progress for your account. Trips are created from the admin workspace; this screen updates as soon as one is assigned."
      />
      <section aria-labelledby="recent-trips-heading">
        <h2 id="recent-trips-heading" className="card-title mb-2">
          Recent completed trips
        </h2>
        {recent.isLoading ? (
          <p className="text-sm text-text-muted">Loading recent trips…</p>
        ) : recent.isError ? (
          <ErrorBlock title="Could not load recent trips" error={recent.error} onRetry={() => void recent.refetch()} />
        ) : (recent.data ?? []).length === 0 ? (
          <p className="text-sm text-text-muted">No completed trips on record.</p>
        ) : (
          <ul className="space-y-2">
            {(recent.data ?? []).map((trip) => (
              <TripSummaryCard key={trip.id} trip={trip} />
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
